import type {
  AffectedComponent,
  Incident,
} from '@buffer-mono/shared-utils/status'
import type { FilteredIncidents } from './filterIncidentsForOrg'
import type { AppArea } from './getActiveAppArea'

const normalize = (value: string): string => value.trim().toLowerCase()

const SOCIAL_NETWORKS_GROUP = 'social networks'

/**
 * `Core Buffer` components that show in every area. Channel connection entry
 * points exist across the whole app.
 */
const GLOBAL_COMPONENTS = new Set(['channel connections'])

/**
 * Status page component name (normalized) → the app areas it shows in, per
 * the agreed mapping in CORE-6506. Note Analytics maps to Insights only —
 * explicitly not the legacy Analyze product.
 */
const COMPONENT_TO_AREAS: Partial<Record<string, readonly AppArea[]>> = {
  // Login-only incidents belong exclusively to the login service. An empty
  // mapping still lets any co-tagged app surface determine visibility.
  login: [],
  publishing: ['publish', 'create'],
  community: ['community'],
  analytics: ['insights'],
  settings: ['settings'],
}

/**
 * Status page group name (normalized) → app areas, for groups whose members
 * all share one mapping. The Developer Platform surface is managed from
 * Settings, so the whole group is scoped there.
 */
const GROUP_TO_AREAS: Partial<Record<string, readonly AppArea[]>> = {
  'developer platform': ['settings'],
}

const isGlobal = (component: AffectedComponent): boolean =>
  GLOBAL_COMPONENTS.has(normalize(component.name))

const shouldShowIncident = (
  incident: Incident,
  area: AppArea,
  unrecognized: Set<string>,
): boolean => {
  const components = incident.affected_components ?? []

  // No component data — we can't reason about it, so fail open.
  if (components.length === 0) {
    return true
  }

  if (components.some(isGlobal)) {
    return true
  }

  // Social Networks components carry no area information (channel scoping
  // handles them); only the co-tagged Buffer surface decides the area.
  const surfaceComponents = components.filter(
    (component) => normalize(component.group_name) !== SOCIAL_NETWORKS_GROUP,
  )

  // Social-only incident — nothing to scope an area on, stay safe.
  if (surfaceComponents.length === 0) {
    return true
  }

  let areaMatch = false
  let sawUnrecognized = false
  // Iterate every surface component (no early return) so unrecognized names
  // are always collected for reporting, even when an area already matched.
  for (const component of surfaceComponents) {
    const areas =
      COMPONENT_TO_AREAS[normalize(component.name)] ??
      GROUP_TO_AREAS[normalize(component.group_name)]
    if (areas === undefined) {
      sawUnrecognized = true
      unrecognized.add(component.name)
      continue
    }
    if (areas.includes(area)) {
      areaMatch = true
    }
  }

  // Show if the incident maps to the area being viewed, or if a name we
  // couldn't classify was present (fail open rather than hide something
  // unknown).
  return areaMatch || sawUnrecognized
}

/**
 * Filters status page incidents to those relevant to the app area currently
 * being viewed: global components (see GLOBAL_COMPONENTS) show everywhere;
 * Login-only incidents are left to the login service;
 * mapped surfaces (see COMPONENT_TO_AREAS and GROUP_TO_AREAS) show only in
 * their areas; Social Networks components carry no area of their own;
 * anything we cannot classify — including an unknown active area — fails
 * open (shown). Returns the relevant incidents plus any component names
 * missing from the map, so the caller can report them and the map can be
 * kept current.
 */
export function filterIncidentsForArea(
  incidents: Incident[],
  area: AppArea | null,
): FilteredIncidents {
  if (area === null) {
    return { relevant: incidents, unrecognizedComponents: [] }
  }
  const unrecognized = new Set<string>()
  const relevant = incidents.filter((incident) =>
    shouldShowIncident(incident, area, unrecognized),
  )
  return { relevant, unrecognizedComponents: [...unrecognized] }
}
